import { LoggingInScreen } from 'components/logging-in-screen';
import { Screen, ScreenContext } from 'components/screen';
import { VerifyIdentityScreen } from 'components/verify-identity-screen';
import { BaseResponseFailure } from 'controllers/controller';
import { Errors } from 'errors';
import { DEFAULT_SAML_ORGANIZATION } from 'helpers/saml-idp-to-public-saml-idp.helper';
import translation from 'helpers/translation';
import loginScreenHTML = require('html-loader!html/login-screen');
import { SAMLIdPModel } from 'models/saml-idp.model';

export class LoginScreen extends Screen {
  public static readonly screenID: string = 'login';
  public static readonly samlListDropdownId: string = 'saml-dropdown';
  public static readonly defaultSamlDropdownOption = DEFAULT_SAML_ORGANIZATION;
  protected idp: SAMLIdPModel[];
  protected samlListDropdown;

  constructor(containerEl: HTMLElement, context: ScreenContext, dialog?: HTMLElement) {
    super(containerEl, context, dialog);
    this.disableInputOnSuccessfulSubmit = true;
  }

  public async renderImpl(): Promise<void> {
    // Call getSession to ensure user isn't already logged in or pending verification.
    try {
      const session = await this.marinSSO.getSession();

      // Does the user have a site identity?
      if (!session.account.identity) {
        // Show login screen despite the user having an identity.
        // TODO: When supported by the API, show a screen allowing the user to link accounts.
        throw new Error();
      }

      // Already logged in.
      this.onSuccess();
    } catch (e) {
      // Trigger identity verification if needed.
      if (e && e.errorMessage === Errors[Errors.TRIGGER_IDENTITY_VERIFICATION]) {
        this.switchScreen(VerifyIdentityScreen.screenID, {
          source: 'triggerIdentityVerification',
          loginID: e.errorDetails.loginID,
          site: e.errorDetails.site,
        });
      } else {
        // Render screen.
        this.containerEl.innerHTML = loginScreenHTML;

        this.samlListDropdown = this.getFormDropdownInput(LoginScreen.samlListDropdownId);
        this.samlListDropdown.hidden = true;

        // Trigger when the login ID changes.
        this.onFormValueChange('loginID', (loginID) => this.onLoginIDChanged(loginID));
      }
    } finally {
      // If api property is missing hide element
      if (!window?.SETTINGS?.api?.endpoint) {
        this.toggleFormElement('msso-text-sign-up', false);
      }
    }
  }
  /**
   * This checks if the user trying to login is tied with a saml IDP
   * 1. eg: idp: []
   * if empty return false
   *
   * 2. eg: [{entityId: 'marin', emailDomain: '@marinsoftware.com'}]
   * First check if dropdown is displayed and its value selected is not None if so go to SAML, if value is none then continue to login password
   *
   */
  public checkForSAMLIdp(): boolean {
    let isSamlIdp = false;
    if (this.idp && this.idp?.length > 0) {
      const entityID = this.getSelectedDropDownValue(LoginScreen.samlListDropdownId) || this.idp[0].entityID;
      if (entityID && entityID !== LoginScreen.defaultSamlDropdownOption) {
        this.marinSSO.loginWithSAMLIdPEntityId({ entityID: entityID });
        isSamlIdp = true;
      }
    }
    return isSamlIdp;
  }

  public async submitImpl(): Promise<void> {
    // Don't even submit to API if its IDP
    if (this.checkForSAMLIdp()) {
      return;
    }

    // Get login ID.
    const loginID = this.getFormValue('loginID');

    // Attempt to login.
    try {
      await this.marinSSO.login({
        loginID,
        password: this.getFormValue('password'),
        entityID: this.getSelectedDropDownValue(LoginScreen.samlListDropdownId),
      });

      this.onSuccess();
    } catch (ex) {
      // Check for failure due to SAML and redirect if necessary.
      const response: BaseResponseFailure = ex;
      if (response.errorMessage === Errors[Errors.CANNOT_BECAUSE_IS_MANAGED_BY_SAML]) {
        const entityID = this.getSelectedDropDownValue(LoginScreen.samlListDropdownId);
        if (entityID) {
          return this.marinSSO.loginWithSAMLIdPEntityId({ entityID });
        }
      }

      throw response;
    }
  }

  protected getTitle(): string {
    return 'Login';
  }

  public onSamlDropdownChange(): void {
    this.onDropDownFormValueChange(LoginScreen.samlListDropdownId, (value) => {
      if (value !== LoginScreen.defaultSamlDropdownOption) {
        this.hidePasswordElement();
      } else {
        this.showPasswordElement();
      }
    });
  }

  /**
   * When the login ID changes, disable the password field if user has SAML IdP.
   * If the user has multiple IDP then disable password field is value is not "None"
   */
  protected async onLoginIDChanged(loginID: string): Promise<void> {
    try {
      const { idp } = await this.marinSSO.getAccountIdP({ loginID });
      this.idp = idp as SAMLIdPModel[];
      let showDropdown = false;
      // If any of the IDP has a showpassword dropdown then render it
      this.idp.forEach(({ attributeMap }) => {
        if (attributeMap?.showPasswordDropdown === 'true') {
          showDropdown = true;
        }
      });
      this.hideSamlListDropdown();
      if ((this.idp && this.idp?.length > 1) || showDropdown) {
        this.createSAMLIdpDropdown(this.idp);
      } else {
        if (this.idp && this.idp?.length === 1) {
          this.hidePasswordElement();
        } else {
          this.showPasswordElement();
        }
      }
    } catch (e) {
      // Login ID doesn't exist. The user may not have finished typing.
      this.showPasswordElement();
    }
  }

  protected createSAMLIdpDropdown(samlIDPList: SAMLIdPModel[]): void {
    const dropDown = document.getElementById(LoginScreen.samlListDropdownId) as HTMLSelectElement;
    this.removeAllDropdownOptions(dropDown);
    this.showSamlListDropdown();
    let selectOptionList = dropDown.options;
    selectOptionList.add(new Option('Please Select Login Method:', '', true, true));
    selectOptionList.add(new Option('Password', LoginScreen.defaultSamlDropdownOption, false, false));
    // Get all countries
    samlIDPList.forEach(({ entityID }) => {
      const translatedVal = translation(`SAML_IDP_${entityID.toUpperCase()}`) || entityID;
      selectOptionList.add(new Option(translatedVal, entityID, false, false));
    });
    this.onSamlDropdownChange();
  }

  protected showSamlListDropdown(): void {
    this.samlListDropdown.hidden = false;
    this.samlListDropdown.required = true;
  }

  protected hideSamlListDropdown(): void {
    this.samlListDropdown.hidden = true;
    this.samlListDropdown.required = false;
  }

  protected showPasswordElement(): void {
    const passwordFormEl = this.getFormInput('password');
    passwordFormEl.hidden = false;
    passwordFormEl.required = true;
  }

  protected hidePasswordElement(): void {
    const passwordFormEl = this.getFormInput('password');
    passwordFormEl.hidden = true;
    passwordFormEl.required = false;
  }
  /**
   * Called after successful login.
   */
  protected onSuccess(): void {
    this.switchScreen(LoggingInScreen.screenID);
  }
}
